import { useState, useEffect, useContext, useMemo } from "react";
import { SiteContext } from "../contexts/SiteContext";
import fetchSite from "../utils/fetchSite";
import { useParams, Link } from "react-router-dom";
import { Paper, Typography, makeStyles, Fade } from "@material-ui/core";
import LayersIcon from "@material-ui/icons/Layers";
import Loading from "../components/Loading";
import Header from "../components/Header";
import AppBar from "../components/AppBar";
import Breadcrumbs from "../components/Breadcrumbs";
import { DataGrid } from "@material-ui/data-grid";
import AdminBar from "../components/AdminBar";
import Banner from "../components/Banner";

function Themes() {
	const [loading, setLoading] = useState(true);
	const [themes, setThemes] = useState();
	const [sortModel, setSortModel] = useState([
		{
			field: "name",
			sort: "asc",
		},
	]);
	const { selectedSite, setSelectedSite } = useContext(SiteContext);
	const { siteSlug } = useParams();

	const useStyles = makeStyles((theme) => ({
		root: {
			flex: 1,
			paddingBottom: "4rem",
		},
		main: {
			margin: "0 auto",
			padding: "1rem",
			paddingTop: 0,
			maxWidth: process.env.REACT_APP_MAX_WIDTH,
		},
		paper: {
			padding: "1rem",
			type: "dark",
		},
		title: {
			color:
				selectedSite && selectedSite.secondaryColor
					? selectedSite.secondaryColor
					: theme.palette.secondary.main,
		},
		datagrid: {
			"& a": {
				textDecoration: "none",
				color: theme.palette.primary.main,
			},
			"& a:visited": {
				textDecoration: "none",
				color: theme.palette.primary.main,
			},
			"& a:hover": {
				textDecoration: "underline",
			},
			//Remove outlines when clicking a cell in the datagrid
			"&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
				outline: 0,
			},
			//Remove outlines when clicking a column header
			"&.MuiDataGrid-root .MuiDataGrid-colCell:focus": {
				outline: 0,
			},
		},
	}));

	const classes = useStyles();

	// This effect sets the selectedSite based on the :siteSlug (first url parameter)
	useEffect(() => {
		fetchSite(siteSlug, setSelectedSite);
	}, [siteSlug, setSelectedSite]);

	// This effect sets the themes and uses the selectedSite._id to make sure it's on the right site
	useEffect(() => {
		async function fetchThemes() {
			let res = await fetch(
				`${process.env.REACT_APP_API_URL}/themes?&site=${selectedSite._id}`,
				{ credentials: "include" }
			);
			res = await res.json();
			if (res.success) {
				setThemes(res.data);
				setLoading(false);
			} else {
				console.error(res.error);
			}
		}
		if (selectedSite) {
			fetchThemes();
		}
	}, [selectedSite]);

	const columns = useMemo(
		() => [
			{
				field: "name",
				headerName: "Name",
				renderCell: (params) => {
					return (
						<Link to={`/${selectedSite.slug}/themes/${params.row.slug}`}>
							{params.row.name}
						</Link>
					);
				},
				flex: 3,
			},
			{
				field: "entries",
				headerName: "Entries",
				filterable: false,
				sortable: false,
				renderCell: (params) => params.value.length || "0",
				type: "number",
				flex: 1,
			},
			{
				field: "people",
				headerName: "People",
				renderCell: (params) => params.value.length || "0",
				type: "number",
				flex: 1,
				filterable: false,
				sortable: false,
			},
		],
		[selectedSite]
	);

	if (loading || !selectedSite || !themes) {
		return <Loading />;
	}

	return (
		<Fade in={typeof themes != "undefined"}>
			<div className={classes.root}>
				<AdminBar />
				<Header />
				<AppBar />
				<Banner />
				<Breadcrumbs
					links={[
						{
							name: "Themes",
							url: `/${selectedSite.slug}/`,
						},
					]}
				/>
				<main className={classes.main}>
					<Paper className={classes.paper}>
						<Typography
							className={classes.title}
							gutterBottom
							component="h2"
							variant="h6"
						>
							<LayersIcon /> All Themes
						</Typography>
						<DataGrid
							className={classes.datagrid}
							getRowId={(row) => row._id}
							autoHeight
							rows={themes}
							columns={columns}
							sortModel={sortModel}
							onSortModelChange={(model) => setSortModel(model)}
							disableSelectionOnClick
						/>
					</Paper>
				</main>
			</div>
		</Fade>
	);
}

export default Themes;
